import {
  ArrayInput,
  AutocompleteInput,
  Breadcrumb,
  DateInput,
  Edit,
  ReferenceInput,
  SelectInput,
  SimpleForm,
  SimpleFormIterator,
  TextInput,
} from "@/components/admin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, ListChecks, Truck } from "lucide-react";
import { required, useRecordContext } from "ra-core";
import { Link } from "react-router-dom";
import { CHALLAN_TYPE_CHOICES, DELIVERY_TERMS_CHOICES, MODE_OF_TRANSPORT_CHOICES, UOM_CHOICES } from "../shared/companyConstants";
import { HSNFieldWrapper, HSNSuggestionsWrapper } from "../shared/HSNAutocomplete";
import { InventoryItemPickerWrapper } from "../shared/InventoryItemPicker";
import { StatusSelectInput } from "../shared/StatusSelectInput";
import { FilteredContactInput } from "../../misc/FilteredContactInput";

const STATUS_CHOICES = [
  { id: "draft", name: "Draft" },
  { id: "sent", name: "Sent" },
  { id: "dispatched", name: "Dispatched" },
  { id: "delivered", name: "Delivered" },
];

const DCEditTitle = () => {
  const record = useRecordContext();
  if (!record) return null;
  return (
    <span className="text-xl font-bold tracking-tight">
      Edit Delivery Challan {record.reference}
    </span>
  );
};

const DCEditBreadcrumb = () => {
  const record = useRecordContext();
  if (!record) return null;
  return (
    <Breadcrumb>
      <Breadcrumb.Item><Link to="/delivery_challans">Delivery Challans</Link></Breadcrumb.Item>
      <Breadcrumb.Item><Link to={`/delivery_challans/${record.id}/show`}>{record.reference}</Link></Breadcrumb.Item>
      <Breadcrumb.PageItem>Edit</Breadcrumb.PageItem>
    </Breadcrumb>
  );
};

export const DCEdit = () => (
  <Edit disableBreadcrumb title={<DCEditTitle />} mutationMode="pessimistic">
    <DCEditBreadcrumb />
    <SimpleForm className="max-w-5xl mx-auto space-y-6">
      <Card className="w-full shadow-sm">
        <CardHeader className="pb-3 border-b bg-muted/50">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-muted-foreground" />
            <CardTitle className="text-lg font-semibold tracking-tight">Challan Details</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="pt-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <TextInput
              source="reference"
              label="Challan No."
              disabled
              className="w-full opacity-70"
              helperText={false}
            />
            <DateInput source="challan_date" label="Challan Date" className="w-full" />
            <SelectInput
              source="challan_type"
              label="Challan Type"
              choices={CHALLAN_TYPE_CHOICES}
              className="w-full"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ReferenceInput source="company_id" reference="companies">
              <AutocompleteInput label="Bill To Company" className="w-full" />
            </ReferenceInput>
            <FilteredContactInput label="Contact" />
          </div>

          {/* X-3: removed dup "Contact Person Name" — the "Contact" dropdown above
              already identifies the person; keep Contact No. as an override. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <TextInput source="contact_no" label="Contact No. (override)" helperText="Leave blank to use the selected contact's number" className="w-full" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ReferenceInput source="project_id" reference="projects">
              <AutocompleteInput label="Linked Project" className="w-full" />
            </ReferenceInput>
            <ReferenceInput source="pi_id" reference="proforma_invoices">
              <AutocompleteInput label="From PI" optionText="reference" className="w-full" />
            </ReferenceInput>
          </div>

          <TextInput
            source="ship_to_address"
            label="Place of Supply / Ship To"
            multiline
            rows={3}
            className="w-full"
          />
        </CardContent>
      </Card>

      <Card className="w-full shadow-sm">
        <CardHeader className="pb-3 border-b">
          <div className="flex items-center gap-2">
            <Truck className="w-5 h-5 text-muted-foreground" />
            <CardTitle className="text-lg font-semibold tracking-tight">Transport</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <SelectInput
              source="mode_of_transport"
              label="Mode of Transport"
              choices={MODE_OF_TRANSPORT_CHOICES}
              className="w-full"
            />
            <TextInput source="vehicle_no" label="Vehicle No." className="w-full" />
            <StatusSelectInput
              source="status"
              label="Status"
              choices={STATUS_CHOICES}
              className="w-full"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <SelectInput
              source="delivery_terms"
              label="Delivery Terms"
              choices={DELIVERY_TERMS_CHOICES}
              helperText="Domestic transport or export/import (Incoterms)"
              className="w-full"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="w-full shadow-sm">
        <CardHeader className="pb-3 border-b">
          <div className="flex items-center gap-2">
            <ListChecks className="w-5 h-5 text-primary" />
            <CardTitle className="text-lg font-semibold tracking-tight">Items</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <ArrayInput source="items" label="">
            <SimpleFormIterator className="flex flex-col gap-4">
              <div className="relative">
                <InventoryItemPickerWrapper setTaxRate={false} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end w-full">
                <div className="md:col-span-2 relative">
                  <TextInput
                    source="description"
                    label="Product Description"
                    validate={required()}
                    helperText={false}
                    className="w-full"
                  />
                  <HSNSuggestionsWrapper />
                </div>
                <HSNFieldWrapper label="HSN / Item" />
                <TextInput
                  source="quantity"
                  label="Quantity"
                  helperText={false}
                />
                <SelectInput
                  source="unit"
                  label="UoM"
                  choices={UOM_CHOICES}
                  helperText={false}
                />
              </div>
              <div className="ml-4 border-l-2 border-border pl-4">
                <p className="text-xs text-muted-foreground mb-2 font-medium">
                  Sub-items
                </p>
                <ArrayInput source="sub_items" label="">
                  <SimpleFormIterator>
                    <TextInput
                      source="text"
                      label="Sub-item"
                      helperText={false}
                      className="w-full"
                    />
                    <TextInput
                      source="qty_note"
                      label="Qty note"
                      helperText={false}
                      className="w-40"
                    />
                  </SimpleFormIterator>
                </ArrayInput>
              </div>
            </SimpleFormIterator>
          </ArrayInput>
        </CardContent>
      </Card>
    </SimpleForm>
  </Edit>
);
